// src/pages/Gathering.tsx
import React, { useState, useEffect } from 'react'
import api from '../api/axios'
import './Gathering.css'
import bgImg     from '../assets/gathering/bg.png'
import buttonImg from '../assets/gathering/button.png'

// prettify keys like "iron_ore" → "Iron Ore", "hp" → "HP"
const prettifyName = (key: string): string => {
  const capitalize = (s: string) =>
    s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()

  return key
    .split('_')
    .map(tok => {
      if (tok.toLowerCase() === 'hp' || tok.toLowerCase() === 'health') return 'HP'
      return capitalize(tok)
    })
    .join(' ')
}

type ToolsMap = {
  pickaxe?: { level: number }
  axe?:     { level: number }
  bow?:     { level: number }
  sickle?:  { level: number }
}

export default function GatheringPage() {
  const [tools, setTools]         = useState<ToolsMap>({})
  const [cooldowns, setCooldowns] = useState<Record<string, number>>({})
  const [msg, setMsg]             = useState<string | null>(null)
  const [now, setNow]             = useState(Date.now())

  // tick every second
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  // load tools once
  useEffect(() => {
    api.get<{ inventory: { tools: ToolsMap } }>('/character')
      .then(res => setTools(res.data.inventory.tools || {}))
      .catch(() => setMsg('Failed to load your tools.'))
  }, [])

  function makeHandler(endpoint: string, toolKey: string) {
    return async () => {
      setMsg(null)
      try {
        const res = await api.post<{
          amount: number
          material: string | string[]
          cooldownRemaining: number
        }>(`/${endpoint}`)

        const { amount, material, cooldownRemaining } = res.data
        // handle single vs multiple materials
        let materialNames: string
        if (Array.isArray(material)) {
          materialNames = material.map(m => prettifyName(m)).join(', ')
        } else {
          materialNames = prettifyName(material)
        }

        setMsg(`+${amount} ${materialNames}!`)
        setCooldowns(cd => ({
          ...cd,
          [toolKey]: Date.now() + cooldownRemaining * 1000
        }))
        setTimeout(() => setMsg(null), 5000)

      } catch (err: any) {
        const errMsg = err.response?.data?.error || err.message
        setMsg(`Error: ${errMsg}`)
        setTimeout(() => setMsg(null), 5000)
      }
    }
  }

  // seconds remaining for toolKey
  const getRemaining = (toolKey: string) => {
    const until = cooldowns[toolKey] || 0
    const secs = Math.ceil((until - now) / 1000)
    return secs > 0 ? secs : 0
  }
  // whether tool is on cooldown
  const isOnCd = (toolKey: string) => getRemaining(toolKey) > 0

  return (
    <div
      className="gathering-page"
      style={{ backgroundImage: `url(${bgImg})` }}
    >
      <h2>Gathering</h2>
      {msg && <div className="message">{msg}</div>}
      <div className="buttons">
        {(['pickaxe','axe','bow','sickle'] as const).map(key => {
          const labels: Record<string,string> = {
            pickaxe: '🛠️ Dig',
            axe:     '🪓 Chop',
            bow:     '🏹 Hunt',
            sickle:  '🌾 Harvest',
          }
          const level = tools[key]?.level
          const baseLabel = `${labels[key]} ${level ? `(Lvl ${level})` : `(no ${key})`}`
          const remaining = getRemaining(key)

          return (
            <button
              key={key}
              className="gather-btn"
              style={{ backgroundImage: `url(${buttonImg})` }}
              onClick={makeHandler(
                key === 'pickaxe' ? 'dig' :
                key === 'axe'     ? 'chop' :
                key === 'bow'     ? 'hunt' : 'harvest',
                key
              )}
              disabled={!level || isOnCd(key)}
            >
              {remaining > 0 ? `⏳ ${remaining}s` : baseLabel}
            </button>
          )
        })}
      </div>
    </div>
  )
}
